import { useEffect, useState } from "react";
import axios from "axios";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { GlobalState } from "../../context/GlobalContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";

const AuthenticatorSection = ({ isDefault, onSetDefault, onDefaultCleared }) => {
	const { snackBar, setSnackBar, setSnackBarOpen } = GlobalState();
	const [enabled, setEnabled] = useState(false);
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState(false);
	const [setup, setSetup] = useState(null);
	const [code, setCode] = useState("");
	const [backupCodes, setBackupCodes] = useState(null);
	const [password, setPassword] = useState("");
	const [disableCode, setDisableCode] = useState("");
	const [showDisable, setShowDisable] = useState(false);

	const showSnackBar = (message, severity = "error") => {
		setSnackBarOpen(true);
		setSnackBar({ ...snackBar, message, severity });
	};

	const loadStatus = async () => {
		try {
			const res = await axios.get("/api/auth2/totp/status");
			setEnabled(Boolean(res?.data?.result?.enabled));
		} catch (error) {
			showSnackBar(parseErrorMessage(error));
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadStatus();
	}, []);

	const startSetup = async () => {
		setBusy(true);
		try {
			const res = await axios.post("/api/auth2/totp/setup");
			setSetup({
				qr: res?.data?.result?.qr_data_url,
				manualKey: res?.data?.result?.manual_key,
			});
			setCode("");
			setBackupCodes(null);
			showSnackBar(res?.data?.message || "Scan the QR code", "success");
		} catch (error) {
			showSnackBar(parseErrorMessage(error));
		} finally {
			setBusy(false);
		}
	};

	const confirmSetup = async () => {
		if (!code.trim()) {
			showSnackBar("Enter the 6-digit code from your authenticator app");
			return;
		}
		setBusy(true);
		try {
			const res = await axios.post("/api/auth2/totp/confirm", { code: code.trim() });
			setEnabled(true);
			setSetup(null);
			setCode("");
			setBackupCodes(res?.data?.result?.backup_codes || []);
			showSnackBar(res?.data?.message || "Authenticator enabled", "success");
		} catch (error) {
			showSnackBar(parseErrorMessage(error));
		} finally {
			setBusy(false);
		}
	};

	const disableAuthenticator = async () => {
		if (!password || !disableCode.trim()) {
			showSnackBar("Enter your Relook password and a current authenticator or backup code");
			return;
		}
		setBusy(true);
		try {
			const res = await axios.post("/api/auth2/totp/disable", {
				password,
				code: disableCode.trim(),
			});
			setEnabled(false);
			setPassword("");
			setDisableCode("");
			setShowDisable(false);
			setBackupCodes(null);
			if (isDefault) onDefaultCleared?.();
			showSnackBar(res?.data?.message || "Authenticator disabled", "success");
		} catch (error) {
			showSnackBar(parseErrorMessage(error));
		} finally {
			setBusy(false);
		}
	};

	const copyBackupCodes = async () => {
		try {
			await navigator.clipboard.writeText((backupCodes || []).join("\n"));
			showSnackBar("Backup codes copied", "success");
		} catch (error) {
			showSnackBar("Could not copy backup codes");
		}
	};

	return (
		<Stack spacing={1.25} sx={{ width: "100%" }}>
			{loading ? (
				<p className="profile-copy">Checking authenticator status...</p>
			) : backupCodes ? (
				<Stack spacing={1.25}>
					<span className="profile-status is-on">On</span>
					<p className="profile-copy">Save these backup codes somewhere safe. Each code works once if you lose the app. They are shown only this time.</p>
					<Box component="pre" className="profile-codes">
						{backupCodes.join("\n")}
					</Box>
					<Button variant="outlined" onClick={copyBackupCodes}>
						Copy codes
					</Button>
					<Button variant="contained" onClick={() => setBackupCodes(null)}>
						I saved these codes
					</Button>
				</Stack>
			) : enabled ? (
				<Stack spacing={1.25}>
					<span className="profile-status is-on">On</span>
					<p className="profile-copy">Use the current 6-digit code from your authenticator app. Email and this laptop stay available on the sign-in screen.</p>
					{isDefault ? <p className="profile-default-note">Default at login</p> : <button type="button" className="profile-default-btn" onClick={onSetDefault}>Set as default</button>}
					{showDisable ? (
						<Stack spacing={1.25}>
							<TextField size="small" type="password" label="Relook password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" fullWidth />
							<TextField
								size="small"
								label="Authenticator or backup code"
								value={disableCode}
								onChange={(e) => setDisableCode(e.target.value)}
								fullWidth
							/>
							<Button variant="outlined" color="warning" disabled={busy} onClick={disableAuthenticator}>
								Turn off authenticator
							</Button>
							<Button disabled={busy} onClick={() => setShowDisable(false)}>
								Cancel
							</Button>
						</Stack>
					) : (
						<Button variant="outlined" color="warning" onClick={() => setShowDisable(true)}>
							Turn off
						</Button>
					)}
				</Stack>
			) : setup ? (
				<Stack spacing={1.25}>
					<p className="profile-copy">Scan this with Google Authenticator, Microsoft Authenticator, or a similar app, then enter the current 6-digit code.</p>
					{setup.qr ? <Box component="img" alt="Authenticator QR code" src={setup.qr} className="profile-qr" /> : null}
					<Typography variant="caption" sx={{ wordBreak: "break-all" }}>
						Manual key: {setup.manualKey}
					</Typography>
					<TextField
						size="small"
						label="6-digit code"
						value={code}
						onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
						inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
						fullWidth
					/>
					<Button variant="contained" disabled={busy} onClick={confirmSetup}>
						Confirm and enable
					</Button>
					<Button disabled={busy} onClick={() => setSetup(null)}>
						Cancel
					</Button>
				</Stack>
			) : (
				<Stack spacing={1.25}>
					<span className="profile-status">Off</span>
					<p className="profile-copy">Optional. After you confirm a code, you can set the authenticator app as the default at login. Email stays available.</p>
					<Button variant="contained" disabled={busy} onClick={startSetup}>
						Set up authenticator
					</Button>
				</Stack>
			)}
		</Stack>
	);
};

export default AuthenticatorSection;
